/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useState, useCallback } from "react";
import type { FocusLocation } from "./type";

export interface UseHandleFocusLocationProps {
  defaultColumnId: string;
  blockedColumnIds?: string[];
}

export const useHandleFocusLocation = ({
  defaultColumnId,
  blockedColumnIds = [],
}: UseHandleFocusLocationProps) => {
  const [focusLocation, setFocusLocation] = useState<FocusLocation>({
    rowId: 0,
    columnId: defaultColumnId,
  });

  const setValidFocusLocation = useCallback(
    (nextLocation: FocusLocation): boolean => {
      const rowIdNum = Number(nextLocation.rowId);
      const colIdNum = Number(nextLocation.columnId);

      // Prevent moving focus out of the grid or to a blocked column
      if (
        Number.isNaN(rowIdNum) ||
        Number.isNaN(colIdNum) ||
        rowIdNum < 0 ||
        colIdNum < 0 ||
        blockedColumnIds.includes(nextLocation.columnId as string)
      ) {
        return false;
      }

      setFocusLocation(nextLocation);
      return true;
    },
    [blockedColumnIds]
  );

  return {
    focusState: { focusLocation, setFocusLocation },
    setValidFocusLocation,
  };
};
